import React, { Fragment } from 'react';
import classNames from 'classnames';
import MailIcon from '@moda/icons/mail-16';
import { Clickable, ClickableProps, Stack, Text } from '@moda/om';
import { LOW_INVENTORY_AMOUNT } from '../../constants';
import { SizeInventoryFragment } from '../../generated/types';
import { usePreferredRegionSizeEquivalents } from './usePreferredRegionSizeEquivalents';
import './SizeSelectorItem.scss';
import { SizeSelectorSizeLabel } from './SizeSelectorSizeLabel';

type Props = ClickableProps & {
  size: SizeInventoryFragment;
  sizeEquivalents: string[][];
  disableSizeMapping: boolean;
  selected?: boolean;
  focused?: boolean;
};

export const SizeSelectorItem: React.FC<Props> = ({
  size,
  sizeEquivalents,
  disableSizeMapping,
  selected,
  focused,
  className,
  ...rest
}) => {
  const equivalents = usePreferredRegionSizeEquivalents(sizeEquivalents, size.size);

  if (!size.size) return null;

  const ariaLabel = size.canSell ? `Size ${size.size}` : `Sold out, Size ${size.size}`;

  return (
    <Clickable
      role="option"
      aria-label={ariaLabel}
      aria-selected={selected}
      aria-disabled={!size.canSell}
      className={classNames(
        'SizeSelectorItem',
        {
          'SizeSelectorItem--selected': selected,
          'SizeSelectorItem--available': size.canSell,
          'SizeSelectorItem--focused': focused
        },
        className
      )}
      {...rest}
    >
      <SizeSelectorSizeLabel
        className="SizeSelectorItem__size"
        size={size.size}
        canSell={size.canSell}
      />
      <div className="SizeSelectorItem__equivalents">
        {!disableSizeMapping &&
          equivalents.map((equivalent, index) => (
            <Fragment key={equivalent}>
              {index > 0 && <Text color={size.canSell ? 'ink' : 'fog'}>, </Text>}
              <SizeSelectorSizeLabel size={equivalent} canSell={size.canSell} />
            </Fragment>
          ))}
      </div>
      {!size.canSell && (
        <Stack direction="horizontal" space={2} alignItems="center">
          <MailIcon />
          <Text>Notify me</Text>
        </Stack>
      )}
      {size.canSell &&
        size.available != null &&
        size.available > 0 &&
        size.available <= LOW_INVENTORY_AMOUNT && (
          <Text color="brick">Only {size.available} left</Text>
        )}
    </Clickable>
  );
};
